import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES, PAGINATION } from '@/constants/apiRoutes'
import { CardManagementList, CardManagementSearchParams, CreateCardParam, CreatedCardResponse, EditCardParam } from '@/types/cardManagement'

export const getCardList = createAsyncThunk<CardManagementList, CardManagementSearchParams>(
    `${API_ROUTES.ADMIN_CARD_LIST_ENDPOINT}`,
    async ({ card_id, card_name, note, page, page_size, display_order, group, card_name_kana }) => {
        // include multiple possible param names for group to be tolerant to backend naming
        const rawParams = {
            card_id: card_id,
            card_name: card_name,
            card_name_kana: card_name_kana, // for backward compatibility with older backend that only supports card_name_kana
            display_order: display_order,
            // common variants: 'group', 'card_group', 'cardGroup'
            group: group,
            card_group: group,
            cardGroup: group,
            note: note,
            page: page,
            page_size: page_size
        }

        const params = new URLSearchParams()
        Object.entries(rawParams).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, String(value))
            }
        })

        const res = await apiClient.get<CardManagementList>(
            `${API_ROUTES.ADMIN_CARD_LIST_ENDPOINT}?${params.toString()}`
        );

        // Normalize payload (support apiClient wrapping)
        const payload = (res as any).data ?? res

        // If caller requested a group filter but backend did not apply it,
        // apply client-side filtering as a fallback (case-insensitive, substring).
        if (group && payload && Array.isArray(payload.items)) {
            const q = String(group).toLowerCase();
            const filtered = payload.items.filter((item: any) => {
                const g = (item?.group ?? '').toString().toLowerCase();
                return g.includes(q);
            });
            payload.items = filtered;
            payload.total = filtered.length;
        }

        return payload
    })

export const createCard = createAsyncThunk<CreatedCardResponse, CreateCardParam>(API_ROUTES.ADMIN_CARD_ENDPOINT, async (createCardParam) => {
    const res = await apiClient.post<CreatedCardResponse>(API_ROUTES.ADMIN_CARD_ENDPOINT, createCardParam);
    return res
})

export const deleteCard = createAsyncThunk<CreatedCardResponse, { cardId: string }>(API_ROUTES.ADMIN_CARD_ENDPOINT, async ({ cardId }) => {
    const res = await apiClient.delete<CreatedCardResponse>(`${API_ROUTES.ADMIN_CARD_ENDPOINT}/${cardId}`);
    return res;
})

export const editCard = createAsyncThunk<CreatedCardResponse, EditCardParam>(API_ROUTES.ADMIN_CARD_ENDPOINT, async (editCardParam) => {
    const { card_id, ...rest } = editCardParam
    const res = await apiClient.put<CreatedCardResponse>(`${API_ROUTES.ADMIN_CARD_ENDPOINT}/${card_id}`, {
        ...rest
    });
    return res
})

const initialState: CardManagementList = {
    items: [],
    total: 0,
    page: PAGINATION.PAGE,
    page_size: PAGINATION.PAGE_SIZE,
    status: 'idle',
    error: null,
    message: '',
    loading: false
}

const cardManagementSlice = createSlice({
    name: 'cardManagement',
    initialState,
    reducers: {
        resetCardManagementState: (state) => {
            state.items = [];
            state.total = 0;
            state.page = PAGINATION.PAGE;
            state.page_size = PAGINATION.PAGE_SIZE;
            state.status = 'idle';
            state.error = null;
            state.message = '';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCardList.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(getCardList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload.items;
                state.page = action.payload.page || PAGINATION.PAGE;
                state.page_size = action.payload.page_size || PAGINATION.PAGE_SIZE;
                state.total = action.payload.total || 0;
                state.message = action.payload.message || '';
                state.loading = false;
            })
            .addCase(getCardList.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.payload as string || action.error.message || 'error';
                state.loading = false;
                state.items = [];
            });
    }
});

export default cardManagementSlice.reducer;
